import { MutableRefObject, useEffect } from 'react';

type SectionRef = MutableRefObject<HTMLAnchorElement | null>;

/**
 * @param {SectionRef[]} sectionRefs - An array of references to the sections that need to be observed.
 * @param {(entry: IntersectionObserverEntry) => void} onIntersect - Callback function to handle intersection events.
 * @param {IntersectionObserverInit} [observerOptions] - Options for the IntersectionObserver.
 */

export const useScrollSpy = (
  sectionRefs: SectionRef[],
  onIntersect: (entry: IntersectionObserverEntry) => void,
  observerOptions?: IntersectionObserverInit,
) => {
  useEffect(() => {
    const validRefs = sectionRefs.filter((ref) => ref.current);
    if (!validRefs.length) return;

    const handleIntersection: IntersectionObserverCallback = (entries) => {
      const [entry] = entries;
      if (!entry.isIntersecting) return;

      onIntersect(entry);
    };

    // Creates observer
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: '-30% 0% -70% 0%', // top, left, bottom, right
      ...observerOptions,
    });

    // Observe each section
    validRefs.forEach((section) => {
      if (section.current) observer.observe(section.current);
    });

    return () => observer.disconnect();
  }, [sectionRefs, onIntersect, observerOptions]);
};
